import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import api from "../lib/api";
import { fileToBase64, inr } from "../lib/format";
import { PrimaryButton, WhyBanner } from "../components/common";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { pc } from "../pricing";
import { ChevronLeft, Camera, Sparkles, Loader2, X, Check } from "lucide-react";

const AMENITIES = ["Lift", "Parking", "Power backup", "Water 24x7", "Security", "Gym", "Wi-Fi", "AC", "Modular kitchen", "Balcony"];
const RULES = ["Family preferred", "Bachelors allowed", "Pets allowed", "Vegetarian only", "No smoking"];

export default function ListingWizard() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [states, setStates] = useState([]);
  const [aiRent, setAiRent] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    title: "", bhk: "1BHK", area_sqft: "", rent: "", deposit: "", furnishing: "Semi-furnished",
    state: "Maharashtra", city: "", locality: "", amenities: [], house_rules: [], tenant_preference: "Anyone",
    photos: [], available_from: "", nearby: "", description: "", visit_slots: [],
  });

  useEffect(() => { api.get("/config/rules").then((r) => setStates(r.data.state_list)); }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files).slice(0, 6);
    const b64 = await Promise.all(files.map(fileToBase64));
    set("photos", [...f.photos, ...b64].slice(0, 6));
  };

  const getAiRent = async () => {
    setAiBusy(true); setAiRent("");
    try {
      const { data } = await api.post("/ai/rent-suggestion", {
        state: f.state, city: f.city, locality: f.locality, bhk: f.bhk,
        area_sqft: Number(f.area_sqft) || 0, furnishing: f.furnishing, amenities: f.amenities, language: lang || "en",
      });
      setAiRent(data.suggestion);
    } catch { setAiRent("Could not fetch suggestion."); } finally { setAiBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const payload = { ...f, area_sqft: Number(f.area_sqft) || 0, rent: Number(f.rent), deposit: Number(f.deposit),
        visit_slots: ["Sat 11:00 AM", "Sat 4:00 PM", "Sun 11:00 AM", "Sun 4:00 PM"] };
      const { data } = await api.post("/listings", payload);
      navigate(`/properties/${data.id}`);
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const steps = [t("basic_info"), t("photos_location"), t("amenities_rules"), t("ai_rent")];

  return (
    <div className="max-w-md md:max-w-4xl mx-auto min-h-screen bg-sand pb-10">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))} data-testid="wizard-back-btn"><ChevronLeft className="w-6 h-6 text-slate-600" /></button>
        <div className="flex-1">
          <div className="font-head font-bold text-slate-900">{t("list_property")}</div>
          <div className="text-xs text-slate-400">{t("step")} {step}/4 · {steps[step - 1]}</div>
        </div>
      </header>
      <Progress value={(step / 4) * 100} className="h-1.5 rounded-none" />

      <div className="px-4 py-5 space-y-4">
        {step === 1 && (
          <div className="space-y-3 gs-rise">
            <Field label={t("title")}><Input className="rounded-xl gs-tap" data-testid="wiz-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="2BHK near Andheri Metro" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("bhk")}><select className="w-full rounded-xl border border-slate-200 gs-tap px-3 text-sm" data-testid="wiz-bhk" value={f.bhk} onChange={(e) => set("bhk", e.target.value)}>{["1RK","1BHK","2BHK","3BHK","4BHK"].map((b) => <option key={b}>{b}</option>)}</select></Field>
              <Field label={t("area")}><Input type="number" className="rounded-xl gs-tap" data-testid="wiz-area" value={f.area_sqft} onChange={(e) => set("area_sqft", e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("rent")}><Input type="number" className="rounded-xl gs-tap" data-testid="wiz-rent" value={f.rent} onChange={(e) => set("rent", e.target.value)} /></Field>
              <Field label={t("deposit")}><Input type="number" className="rounded-xl gs-tap" data-testid="wiz-deposit" value={f.deposit} onChange={(e) => set("deposit", e.target.value)} /></Field>
            </div>
            <Field label={t("furnishing")}><select className="w-full rounded-xl border border-slate-200 gs-tap px-3 text-sm" data-testid="wiz-furnishing" value={f.furnishing} onChange={(e) => set("furnishing", e.target.value)}>{["Unfurnished","Semi-furnished","Fully furnished"].map((x) => <option key={x}>{x}</option>)}</select></Field>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 gs-rise">
            <Field label={t("add_photos")}>
              <label data-testid="wiz-photo-upload" className="gs-card border-2 border-dashed border-slate-300 flex flex-col items-center justify-center py-6 cursor-pointer text-slate-400">
                <Camera className="w-7 h-7 mb-1" /><span className="text-sm">{t("add_photos")}</span>
                <input type="file" accept="image/*" multiple hidden onChange={onPhotos} />
              </label>
              {f.photos.length > 0 && <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">{f.photos.map((p, i) => (
                <div key={p.slice(0, 48)} className="relative shrink-0"><img src={p} alt="" className="w-20 h-20 rounded-xl object-cover" /><button onClick={() => set("photos", f.photos.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button></div>
              ))}</div>}
            </Field>
            <Field label={t("state")}><select className="w-full rounded-xl border border-slate-200 gs-tap px-3 text-sm" data-testid="wiz-state" value={f.state} onChange={(e) => set("state", e.target.value)}>{states.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("city")}><Input className="rounded-xl gs-tap" data-testid="wiz-city" value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
              <Field label={t("locality")}><Input className="rounded-xl gs-tap" data-testid="wiz-locality" value={f.locality} onChange={(e) => set("locality", e.target.value)} /></Field>
            </div>
            <Field label={t("nearby")}><Input className="rounded-xl gs-tap" data-testid="wiz-nearby" value={f.nearby} onChange={(e) => set("nearby", e.target.value)} placeholder="Metro 400m, DAV School 1km" /></Field>
            <Field label={t("available_from")}><Input type="date" className="rounded-xl gs-tap" data-testid="wiz-available" value={f.available_from} onChange={(e) => set("available_from", e.target.value)} /></Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 gs-rise">
            <Field label={t("amenities")}>
              <div className="flex flex-wrap gap-2">{AMENITIES.map((a) => (
                <button key={a} onClick={() => toggle("amenities", a)} data-testid={`amenity-${a}`} className={`px-3 py-2 rounded-full text-sm border ${f.amenities.includes(a) ? "bg-terracotta text-white border-terracotta" : "bg-white text-slate-600 border-slate-200"}`}>{a}</button>
              ))}</div>
            </Field>
            <Field label={t("house_rules")}>
              <div className="flex flex-wrap gap-2">{RULES.map((r) => (
                <button key={r} onClick={() => toggle("house_rules", r)} data-testid={`rule-${r}`} className={`px-3 py-2 rounded-full text-sm border ${f.house_rules.includes(r) ? "bg-terracotta text-white border-terracotta" : "bg-white text-slate-600 border-slate-200"}`}>{r}</button>
              ))}</div>
            </Field>
            <WhyBanner>{t("discrimination_warn")}</WhyBanner>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 gs-rise">
            <button onClick={getAiRent} disabled={aiBusy} data-testid="ai-rent-estimator-btn" className="gs-tap w-full rounded-xl bg-slate2 text-white font-semibold flex items-center justify-center gap-2">
              {aiBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />} {t("get_ai_rent")}
            </button>
            {aiRent && <div className="gs-card p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="ai-rent-result"><div className="flex items-center gap-1.5 text-terracotta font-semibold mb-2 text-xs"><Sparkles className="w-3.5 h-3.5" /> Sathi AI</div>{aiRent}</div>}
            <div className="gs-card p-4 text-sm space-y-1.5">
              <Row k={t("rent")} v={inr(Number(f.rent))} /><Row k={t("deposit")} v={inr(Number(f.deposit))} /><Row k={t("bhk")} v={f.bhk} /><Row k={t("city")} v={`${f.locality}, ${f.city}`} />
            </div>
            <p className="text-xs text-trust font-semibold text-center" data-testid="match-disclosure">{pc(lang, "match_disclosure")}</p>
            <PrimaryButton onClick={publish} disabled={busy || !f.title || !f.rent} data-testid="wiz-publish-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t("publish")}</PrimaryButton>
          </div>
        )}

        {step < 4 && <PrimaryButton onClick={() => setStep(step + 1)} data-testid="wiz-next-btn" className="mt-2">{t("next")}</PrimaryButton>}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => <div><label className="text-xs font-semibold text-slate-500 mb-1.5 block">{label}</label>{children}</div>;
const Row = ({ k, v }) => <div className="flex justify-between"><span className="text-slate-400">{k}</span><span className="font-semibold text-slate-800">{v}</span></div>;
